import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch, apiUrl, resolveMediaUrl } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { Modal } from '@/components/ui/Modal';
import { SegmentedTabs } from '@/components/ui/SegmentedTabs';
import { inputClass, labelClass } from '@/components/ui/inputStyles';
import { buildCatalogSiteOptionVMs, type CatalogSiteOptionVM } from '@/lib/catalogSiteTree';

type SiteRow = { id: number; site_code: string; site_name: string; parent_site_id?: number | null };

type PromoAreaOption = { id: number; name: string; enabled: boolean };

type StaleSiteBrief = { id: number; site_name: string; enabled?: boolean };

type RobotRow = {
  id: number;
  miniprogram_id: number;
  robot_name: string;
  enabled: boolean;
  enable_moments_forward: boolean;
  enable_group_forward: boolean;
  moments_run_time_range?: string;
  group_run_time_range?: string;
  forward_content_template?: string | null;
  forward_template_use_moments?: boolean;
  forward_template_use_groups?: boolean;
  forward_within_days?: number;
  promotion_area_ids?: number[];
  mp_enable_moments_forward?: boolean;
  mp_enable_group_forward?: boolean;
  mp_moments_run_time_range?: string;
  mp_group_run_time_range?: string;
  mp_forward_content_template?: string | null;
  mp_default_moments_image_path?: string | null;
  mp_forward_within_days?: number;
  site_ids: number[];
  stale_site_ids?: number[];
  stale_sites?: StaleSiteBrief[];
  created_at: string;
};

const MP_TPL_KEYS = ['display_text', 'contact_phone', 'job_title', 'salary', 'recruiter', 'job_description', 'promotion_text'];

/** 小程序转发任务：Android 发圈附言 / 群发兜底粘贴文案（变量由服务端按信息字段替换） */
const MINIPRO_FORWARD_TPL_VARS: { key: string; label: string; hint: string }[] = [
  { key: 'title_text', label: '标题', hint: '信息标题（title_text）' },
  { key: 'body_text', label: '正文', hint: '信息正文（body_text）' },
  { key: 'contact_phone', label: '联系电话', hint: '信息联系电话' },
  { key: 'promotion_text', label: '标题+正文', hint: '标题与正文合并（换行连接）' },
  { key: 'image_urls', label: '图片链接', hint: '配图下载地址，多行排列' },
];

type RobotFormTab = 'basic' | 'miniprogram' | 'micropromo';

const ROBOT_FORM_TABS: { id: RobotFormTab; label: string }[] = [
  { id: 'basic', label: '基础' },
  { id: 'miniprogram', label: '小程序转发' },
  { id: 'micropromo', label: '微推广' },
];

/** 将保存机器人时的接口 detail 转为面向用户的中文说明。 */
function formatWesRobotSaveError(err?: string, detail?: string): string {
  const d = (detail || '').trim();
  if (
    d.includes('没有可用的转发站点') ||
    d.includes('原勾选的站点可能已关闭') ||
    d === 'site_not_in_miniprogram'
  ) {
    return d.length > 20
      ? d
      : '当前没有可用的转发站点。请在「小程序转发」标签中至少勾选一个有效站点（站点需已启用且在本小程序展示范围内）。';
  }
  if (d === 'miniprogram_robot_name_and_site_ids') {
    return '请填写机器人名称，并在「小程序转发」标签中至少勾选一个转发站点。';
  }
  if (d === 'site_ids') {
    return '请在「小程序转发」标签中至少勾选一个转发站点。';
  }
  if (d) return d;
  if (err === 'invalid_params') return '参数不完整或无效，请检查各标签页必填项。';
  return err || '保存失败';
}

function partitionRobotSiteIds(
  allIds: number[],
  selectableIds: Set<number>,
): { valid: number[]; stale: number[] } {
  const valid: number[] = [];
  const stale: number[] = [];
  for (const id of allIds) {
    if (selectableIds.has(id)) valid.push(id);
    else stale.push(id);
  }
  return { valid, stale };
}

function staleSiteLabel(
  id: number,
  staleSites?: StaleSiteBrief[],
  sites?: SiteRow[],
): string {
  const fromApi = staleSites?.find((s) => s.id === id);
  if (fromApi?.site_name?.trim()) return `${fromApi.site_name.trim()} #${id}`;
  const row = sites?.find((s) => s.id === id);
  if (row?.site_name?.trim()) return `${row.site_name.trim()} #${id}`;
  return `站点 #${id}`;
}

function formatRemovedStaleSitesMessage(removed: number[]): string {
  if (removed.length === 0) return '';
  return `已自动移除 ${removed.length} 个失效转发站点（站点已关闭或已从本小程序展示范围移除）。`;
}

type RobotListResp = { ok: boolean; items: RobotRow[]; total: number; page: number; per_page: number };

type WesRobotsAdminPageProps = {
  miniprogramId: number;
  /** 嵌入小程序详情页时不显示外层标题与返回链接 */
  embedded?: boolean;
};

export function WesRobotsAdminPage({ miniprogramId, embedded }: WesRobotsAdminPageProps) {
  const [searchParams] = useSearchParams();
  const focusSiteId = Math.max(0, parseInt(searchParams.get('site_id') || '0', 10) || 0);

  const [sites, setSites] = useState<SiteRow[]>([]);
  const [data, setData] = useState<RobotRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [listMsg, setListMsg] = useState('');
  const [keyword, setKeyword] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const perPage = 10;

  const [modalOpen, setModalOpen] = useState(false);
  const [formTab, setFormTab] = useState<RobotFormTab>('basic');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [robotName, setRobotName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [enableMoments, setEnableMoments] = useState(true);
  const [enableGroup, setEnableGroup] = useState(true);
  const [momentsRange, setMomentsRange] = useState('07:00-22:00');
  const [groupRange, setGroupRange] = useState('07:00-22:00');
  const [forwardWithinDays, setForwardWithinDays] = useState(7);
  const [siteIdsSel, setSiteIdsSel] = useState<Record<number, boolean>>({});
  const [tpl, setTpl] = useState('');
  const [tplUseMoments, setTplUseMoments] = useState(false);
  const [tplUseGroups, setTplUseGroups] = useState(false);
  const [formMsg, setFormMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [promoAreas, setPromoAreas] = useState<PromoAreaOption[]>([]);
  const [areaIdsSel, setAreaIdsSel] = useState<Record<number, boolean>>({});
  const [mpEnableMoments, setMpEnableMoments] = useState(true);
  const [mpEnableGroup, setMpEnableGroup] = useState(true);
  const [mpMomentsRange, setMpMomentsRange] = useState('07:00-22:00');
  const [mpGroupRange, setMpGroupRange] = useState('07:00-22:00');
  const [mpForwardWithinDays, setMpForwardWithinDays] = useState(7);
  const [mpUseTpl, setMpUseTpl] = useState(false);
  const [mpTpl, setMpTpl] = useState('');
  const [mpUseImg, setMpUseImg] = useState(false);
  const [mpImgPath, setMpImgPath] = useState('');
  const [mpImgPreview, setMpImgPreview] = useState('');
  const [mpImgUploading, setMpImgUploading] = useState(false);
  /** 编辑弹窗内展示的失效站点（不可勾选，保存时不会提交） */
  const [formStaleSites, setFormStaleSites] = useState<StaleSiteBrief[]>([]);
  /** 与 get_miniprogram.entry_site_options 一致：含勾选一级下自动纳入的二级站 */
  const [entrySiteOptions, setEntrySiteOptions] = useState<Array<{ id: number; site_name: string }>>([]);

  const loadSites = useCallback(async () => {
    if (miniprogramId < 1) {
      setSites([]);
      setEntrySiteOptions([]);
      return;
    }
    const mp = await apiFetch<{
      ok: boolean;
      miniprogram?: {
        site_ids: number[];
        entry_site_options?: Array<{ id: number; site_name: string }>;
      };
    }>('get_miniprogram', {
      method: 'GET',
      query: { id: miniprogramId },
    });
    if (!mp.ok || !mp.miniprogram) {
      setSites([]);
      setEntrySiteOptions([]);
      return;
    }
    const opts = Array.isArray(mp.miniprogram.entry_site_options) ? mp.miniprogram.entry_site_options : [];
    const legacyIds = mp.miniprogram.site_ids || [];
    const scopeIds = new Set<number>(
      opts.length > 0 ? opts.map((o) => o.id) : legacyIds.map((x) => Number(x)).filter((x) => x > 0),
    );
    setEntrySiteOptions(
      opts.length > 0 ? opts.map((o) => ({ id: Number(o.id), site_name: String(o.site_name ?? '') })) : legacyIds.map((id) => ({ id: Number(id), site_name: '' })).filter((o) => o.id > 0),
    );
    const r = await apiFetch<{ ok: boolean; items: SiteRow[] }>('list_sites', {
      method: 'GET',
      query: { page: 1, per_page: 100 },
    });
    if (!r.ok) {
      if (opts.length > 0) {
        setSites(
          opts.map((o) => ({
            id: Number(o.id),
            site_code: '',
            site_name: String(o.site_name ?? `站点 #${o.id}`),
            parent_site_id: null,
          })),
        );
      } else {
        setSites([]);
      }
      return;
    }
    let rows = r.items.filter((s) => scopeIds.has(s.id));
    const have = new Set(rows.map((s) => s.id));
    for (const o of opts) {
      const oid = Number(o.id);
      if (oid > 0 && !have.has(oid)) {
        rows.push({
          id: oid,
          site_code: '',
          site_name: String(o.site_name ?? `站点 #${oid}`),
          parent_site_id: null,
        });
        have.add(oid);
      }
    }
    setSites(rows);
  }, [miniprogramId]);

  const loadPromoAreas = useCallback(async () => {
    const r = await apiFetch<{ ok: boolean; items: PromoAreaOption[] }>('list_promotion_areas', {
      method: 'GET',
      query: { page: 1, per_page: 100 },
    });
    if (r.ok) setPromoAreas(r.items);
    else setPromoAreas([]);
  }, []);

  useEffect(() => {
    void loadPromoAreas();
  }, [loadPromoAreas]);

  const load = useCallback(async () => {
    if (miniprogramId < 1) return;
    setLoading(true);
    try {
      const r = await apiFetch<RobotListResp>('list_wes_robots', {
        method: 'GET',
        query: {
          miniprogram_id: miniprogramId,
          page,
          per_page: perPage,
          ...(appliedQ.trim() ? { q: appliedQ.trim() } : {}),
        },
      });
      if (r.ok) {
        setData(r.items);
        setTotal(r.total);
      }
    } finally {
      setLoading(false);
    }
  }, [miniprogramId, page, perPage, appliedQ]);

  useEffect(() => {
    void loadSites();
  }, [loadSites]);

  useEffect(() => {
    void load();
  }, [load]);

  const pages = Math.max(1, Math.ceil(total / perPage));

  const robotSiteSelectRows = useMemo((): CatalogSiteOptionVM[] => {
    const base =
      entrySiteOptions.length > 0
        ? entrySiteOptions.map((o) => ({
            id: o.id,
            site_name: o.site_name.trim()
              ? o.site_name
              : sites.find((s) => s.id === o.id)?.site_name?.trim() || `站点 #${o.id}`,
          }))
        : sites.map((s) => ({ id: s.id, site_name: s.site_name }));
    if (base.length === 0) return [];
    return buildCatalogSiteOptionVMs(base, sites);
  }, [entrySiteOptions, sites]);

  const siteTreeLabelById = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of robotSiteSelectRows) {
      m.set(r.id, r.treeLabel);
    }
    return m;
  }, [robotSiteSelectRows]);

  const selectableSiteIdSet = useMemo(
    () => new Set(robotSiteSelectRows.map((r) => r.id)),
    [robotSiteSelectRows],
  );

  function effectiveRobotSiteIds(row: RobotRow): number[] {
    const stale = new Set(row.stale_site_ids ?? []);
    if (stale.size === 0) {
      return row.site_ids.filter((id) => selectableSiteIdSet.has(id));
    }
    return row.site_ids.filter((id) => !stale.has(id));
  }

  const uploadSiteId = useMemo(() => {
    const picked = Object.entries(siteIdsSel).filter(([, v]) => v).map(([k]) => parseInt(k, 10));
    if (picked.length) return picked[0];
    if (focusSiteId > 0 && robotSiteSelectRows.some((r) => r.id === focusSiteId)) return focusSiteId;
    return robotSiteSelectRows[0]?.id ?? 0;
  }, [siteIdsSel, focusSiteId, robotSiteSelectRows]);

  function resetForm() {
    setEditingId(null);
    setRobotName('');
    setEnabled(true);
    setEnableMoments(true);
    setEnableGroup(true);
    setMomentsRange('07:00-22:00');
    setGroupRange('07:00-22:00');
    setForwardWithinDays(7);
    const init: Record<number, boolean> = {};
    const seed =
      focusSiteId > 0 && robotSiteSelectRows.some((r) => r.id === focusSiteId)
        ? focusSiteId
        : robotSiteSelectRows[0]?.id ?? 0;
    if (seed > 0) init[seed] = true;
    setSiteIdsSel(init);
    setTpl('');
    setTplUseMoments(false);
    setTplUseGroups(false);
    setAreaIdsSel({});
    setMpEnableMoments(true);
    setMpEnableGroup(true);
    setMpMomentsRange('07:00-22:00');
    setMpGroupRange('07:00-22:00');
    setMpForwardWithinDays(7);
    setMpUseTpl(false);
    setMpTpl('');
    setMpUseImg(false);
    setMpImgPath('');
    setMpImgPreview('');
    setFormStaleSites([]);
    setFormTab('basic');
    setFormMsg('');
  }

  function closeModal() {
    if (saving) return;
    setModalOpen(false);
    resetForm();
  }

  function openAdd() {
    resetForm();
    if (focusSiteId > 0 && robotSiteSelectRows.some((r) => r.id === focusSiteId)) {
      setSiteIdsSel({ [focusSiteId]: true });
    }
    setModalOpen(true);
  }

  function openEdit(row: RobotRow) {
    setEditingId(row.id);
    setRobotName(row.robot_name);
    setEnabled(row.enabled);
    setEnableMoments(row.enable_moments_forward);
    setEnableGroup(row.enable_group_forward);
    setMomentsRange((row.moments_run_time_range || '07:00-22:00').trim());
    setGroupRange((row.group_run_time_range || '07:00-22:00').trim());
    const days = row.forward_within_days ?? 7;
    setForwardWithinDays(days >= 1 && days <= 180 ? days : 7);
    const { valid, stale } = partitionRobotSiteIds(row.site_ids, selectableSiteIdSet);
    const m: Record<number, boolean> = {};
    for (const sid of valid) m[sid] = true;
    setSiteIdsSel(m);
    const staleBrief: StaleSiteBrief[] =
      row.stale_sites && row.stale_sites.length > 0
        ? row.stale_sites.filter((s) => stale.includes(s.id))
        : stale.map((id) => {
            const s = sites.find((x) => x.id === id);
            return { id, site_name: s?.site_name ?? '', enabled: false };
          });
    setFormStaleSites(staleBrief);
    const tplStr = row.forward_content_template != null ? String(row.forward_content_template) : '';
    setTpl(tplStr);
    setTplUseMoments(row.forward_template_use_moments === true);
    setTplUseGroups(row.forward_template_use_groups === true);
    const am: Record<number, boolean> = {};
    for (const aid of row.promotion_area_ids ?? []) am[aid] = true;
    setAreaIdsSel(am);
    const mpMomentsOn = row.mp_enable_moments_forward !== false;
    const mpGroupOn = row.mp_enable_group_forward !== false;
    setMpEnableMoments(mpMomentsOn);
    setMpEnableGroup(mpGroupOn);
    setMpMomentsRange((row.mp_moments_run_time_range || '07:00-22:00').trim());
    setMpGroupRange((row.mp_group_run_time_range || '07:00-22:00').trim());
    const mpDays = row.mp_forward_within_days ?? 7;
    setMpForwardWithinDays(mpDays >= 1 && mpDays <= 180 ? mpDays : 7);
    const mpTplVal = row.mp_forward_content_template;
    const hasMpTpl = mpTplVal != null && String(mpTplVal).trim() !== '';
    setMpUseTpl(hasMpTpl);
    setMpTpl(hasMpTpl ? String(mpTplVal) : '');
    const mpDm = row.mp_default_moments_image_path;
    const hasMpDm = mpDm != null && String(mpDm).trim() !== '';
    setMpUseImg(hasMpDm);
    if (hasMpDm) {
      const p = String(mpDm).trim();
      setMpImgPath(p);
      setMpImgPreview(resolveMediaUrl(p));
    } else {
      setMpImgPath('');
      setMpImgPreview('');
    }
    setFormMsg('');
    setModalOpen(true);
  }

  async function onPickMpImage(file: File | null) {
    if (!file || uploadSiteId < 1) {
      setFormMsg(uploadSiteId < 1 ? '请先勾选至少一个站点（用于上传落盘目录）' : '');
      return;
    }
    setMpImgUploading(true);
    setFormMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', apiUrl('upload_manual_entry_image', { site_id: uploadSiteId }));
      xhr.withCredentials = true;
      const p = await new Promise<string>((resolve, reject) => {
        xhr.onload = () => {
          try {
            const j = JSON.parse(xhr.responseText) as { ok?: boolean; local_path?: string; err?: string };
            if (xhr.status >= 200 && xhr.status < 300 && j.ok && j.local_path) resolve(j.local_path);
            else reject(new Error(j.err || '上传失败'));
          } catch {
            reject(new Error('上传失败'));
          }
        };
        xhr.onerror = () => reject(new Error('网络错误'));
        xhr.send(fd);
      });
      setMpUseImg(true);
      setMpImgPath(p);
      setMpImgPreview(resolveMediaUrl(p));
    } catch (e) {
      setFormMsg(e instanceof Error ? e.message : '上传失败');
    } finally {
      setMpImgUploading(false);
    }
  }

  async function submitForm() {
    const name = robotName.trim();
    if (!name) {
      setFormTab('basic');
      setFormMsg('请填写机器人名称');
      return;
    }
    const sids = Object.entries(siteIdsSel)
      .filter(([, v]) => v)
      .map(([k]) => parseInt(k, 10))
      .filter((x) => x > 0 && selectableSiteIdSet.has(x));
    if (sids.length === 0) {
      setFormTab('miniprogram');
      setFormMsg(
        formStaleSites.length > 0
          ? '原勾选的转发站点均已失效，请至少重新勾选一个有效站点'
          : '请至少选择一个需要转发的站点',
      );
      return;
    }
    if (mpUseImg && !mpImgPath.trim()) {
      setFormTab('micropromo');
      setFormMsg('已勾选微推广专用发圈默认图，请先上传或取消勾选');
      return;
    }
    if ((tplUseMoments || tplUseGroups) && !tpl.trim()) {
      setFormTab('miniprogram');
      setFormMsg('已开启自定义转发正文，请填写模板内容或关闭对应开关');
      return;
    }
    const daysNorm = Math.min(180, Math.max(1, Math.round(forwardWithinDays) || 7));
    const mpDaysNorm = Math.min(180, Math.max(1, Math.round(mpForwardWithinDays) || 7));
    const promoAreaIds = Object.entries(areaIdsSel)
      .filter(([, v]) => v)
      .map(([k]) => parseInt(k, 10))
      .filter((x) => x > 0);
    setSaving(true);
    setFormMsg('');
    try {
      const payload = {
        miniprogram_id: miniprogramId,
        robot_name: name,
        enabled,
        enable_moments_forward: enableMoments,
        enable_group_forward: enableGroup,
        moments_run_time_range: momentsRange.trim(),
        group_run_time_range: groupRange.trim(),
        forward_content_template: tpl.trim() || null,
        forward_template_use_moments: tplUseMoments,
        forward_template_use_groups: tplUseGroups,
        default_moments_image_path: null,
        forward_within_days: daysNorm,
        site_ids: sids,
        promotion_area_ids: promoAreaIds,
        mp_enable_moments_forward: mpEnableMoments,
        mp_enable_group_forward: mpEnableGroup,
        mp_moments_run_time_range: mpMomentsRange.trim(),
        mp_group_run_time_range: mpGroupRange.trim(),
        mp_forward_content_template: mpUseTpl ? mpTpl : null,
        mp_default_moments_image_path: mpUseImg ? mpImgPath.trim() : null,
        mp_forward_within_days: mpDaysNorm,
      };
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        id?: number;
        detail?: string;
        removed_stale_site_ids?: number[];
      }>(editingId ? 'update_wes_robot' : 'create_wes_robot', {
        method: 'POST',
        body: JSON.stringify(editingId ? { id: editingId, ...payload } : payload),
      });
      if (r.ok) {
        const pruneMsg = formatRemovedStaleSitesMessage(r.removed_stale_site_ids ?? []);
        setListMsg(pruneMsg || (editingId ? '已保存' : '已新增机器人'));
        setModalOpen(false);
        resetForm();
        await load();
      } else {
        const d = (r.detail || '').trim();
        if (d.includes('没有可用的转发站点') || d.includes('原勾选的站点可能已关闭')) {
          setFormTab('miniprogram');
        }
        setFormMsg(formatWesRobotSaveError(r.err, d));
      }
    } catch (e) {
      setFormMsg(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }

  async function toggleRow(row: RobotRow, next: boolean) {
    setListMsg('');
    const effSiteIds = effectiveRobotSiteIds(row);
    const r = await apiFetch<{ ok: boolean; err?: string; removed_stale_site_ids?: number[] }>('update_wes_robot', {
      method: 'POST',
      body: JSON.stringify({
        id: row.id,
        miniprogram_id: row.miniprogram_id || miniprogramId,
        robot_name: row.robot_name,
        enabled: next,
        enable_moments_forward: row.enable_moments_forward,
        enable_group_forward: row.enable_group_forward,
        moments_run_time_range: (row.moments_run_time_range || '07:00-22:00').trim(),
        group_run_time_range: (row.group_run_time_range || '07:00-22:00').trim(),
        forward_content_template: row.forward_content_template ?? null,
        forward_template_use_moments: row.forward_template_use_moments === true,
        forward_template_use_groups: row.forward_template_use_groups === true,
        forward_within_days: row.forward_within_days ?? 7,
        ...(effSiteIds.length > 0 ? { site_ids: effSiteIds } : {}),
        promotion_area_ids: row.promotion_area_ids ?? [],
        mp_enable_moments_forward: row.mp_enable_moments_forward !== false,
        mp_enable_group_forward: row.mp_enable_group_forward !== false,
        mp_moments_run_time_range: (row.mp_moments_run_time_range || '07:00-22:00').trim(),
        mp_group_run_time_range: (row.mp_group_run_time_range || '07:00-22:00').trim(),
        mp_forward_content_template: row.mp_forward_content_template ?? null,
        mp_default_moments_image_path: row.mp_default_moments_image_path ?? null,
        mp_forward_within_days: row.mp_forward_within_days ?? 7,
      }),
    });
    const pruneMsg = formatRemovedStaleSitesMessage(r.removed_stale_site_ids ?? []);
    setListMsg(r.ok ? pruneMsg || (next ? '已启用' : '已停用') : r.err || '更新失败');
    if (r.ok) await load();
  }

  async function removeRow(row: RobotRow) {
    if (!window.confirm(`确定删除机器人「${row.robot_name}」(#${row.id})？`)) return;
    setListMsg('');
    const r = await apiFetch<{ ok: boolean; err?: string }>('delete_wes_robot', {
      method: 'POST',
      body: JSON.stringify({ id: row.id }),
    });
    setListMsg(r.ok ? '已删除' : r.err || '删除失败');
    if (r.ok) await load();
  }

  if (miniprogramId < 1) {
    return <p className="text-sm text-rose-700">缺少小程序上下文</p>;
  }

  const inner = (
    <>
      {!embedded ? (
        <PageHeader
          title="机器人列表"
          description="机器人归属于当前小程序，仅可勾选该小程序下的站点作为转发范围。Android 端优先使用小程序级密钥 + 机器人编号；仍兼容站点级密钥。"
          actions={
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="primary" onClick={openAdd}>
                新增机器人
              </Button>
              <Link to={`/robot-management?mp_id=${miniprogramId}`}>
                <Button type="button" variant="secondary">
                  返回机器人管理
                </Button>
              </Link>
            </div>
          }
        />
      ) : null}
      <Card title="机器人" titleExtra={<span className="text-sm text-slate-500">共 {total} 条</span>}>
        {listMsg ? (
          <p className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{listMsg}</p>
        ) : null}
        <p className="mb-4 rounded-lg border border-sky-100 bg-sky-50/80 px-3 py-2.5 text-xs leading-relaxed text-sky-950">
          <span className="font-medium">发圈/群发时段：</span>
          「小程序转发」与「微推广」各有一套独立时段（格式如 07:00-22:00，在编辑弹窗对应标签页配置）。
          Android 机器人在时段外不会<strong className="font-medium">新启动</strong>发圈或群发；若某次发圈/群发已在微信中执行，会跑完后再停。
          下列每条机器人会分别列出两套时段，请勿与另一套混淆。
        </p>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {embedded ? (
            <Button type="button" variant="primary" onClick={openAdd}>
              新增机器人
            </Button>
          ) : null}
          <input
            type="search"
            className={inputClass + ' max-w-xs'}
            placeholder="搜索名称"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <Button type="button" variant="secondary" onClick={() => { setAppliedQ(keyword.trim()); setPage(1); }}>
            查询
          </Button>
        </div>
        {loading ? (
          <Spinner label="加载中" />
        ) : data.length === 0 ? (
          <EmptyState>暂无机器人</EmptyState>
        ) : (
          <ul className="space-y-3">
            {data.map((row) => {
              /** 停用机器人时渠道均不生效，列表上按「全关」展示，避免与后台勾选状态混淆 */
              const momentsOn = row.enabled && row.enable_moments_forward;
              const groupsOn = row.enabled && row.enable_group_forward;
              const staleSet = new Set(row.stale_site_ids ?? []);
              const activeSiteIds = row.site_ids.filter((id) => !staleSet.has(id));
              const siteLabels = activeSiteIds.map((id) => {
                const tree = siteTreeLabelById.get(id);
                if (tree) return tree;
                const s = sites.find((x) => x.id === id);
                return s ? `${s.site_name} #${s.id}` : `#${id}`;
              });
              const staleLabels = (row.stale_sites?.length ? row.stale_sites : staleSet.size ? [...staleSet].map((id) => ({ id, site_name: '' })) : []).map(
                (s) => staleSiteLabel(s.id, row.stale_sites, sites),
              );
              return (
                <li
                  key={row.id}
                  className={`rounded-xl border p-4 shadow-sm transition-colors ${
                    row.enabled
                      ? 'border-slate-200/90 bg-white hover:border-slate-300'
                      : 'border-slate-200 bg-slate-50/95 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-slate-600">
                          #{row.id}
                        </span>
                        <span className={`text-base font-semibold ${row.enabled ? 'text-slate-900' : 'text-slate-600'}`}>
                          {row.robot_name}
                        </span>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            row.enabled
                              ? 'bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-200'
                              : 'bg-slate-200/80 text-slate-600 ring-1 ring-inset ring-slate-300'
                          }`}
                        >
                          {row.enabled ? '启用中' : '已停用'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <span
                          className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${
                            momentsOn
                              ? 'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-200'
                              : 'bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200'
                          }`}
                        >
                          朋友圈
                          {momentsOn ? '·开' : '·关'}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${
                            groupsOn
                              ? 'bg-violet-50 text-violet-800 ring-1 ring-inset ring-violet-200'
                              : 'bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200'
                          }`}
                        >
                          微信群发
                          {groupsOn ? '·开' : '·关'}
                        </span>
                        <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 ring-1 ring-inset ring-amber-200/80">
                          近 {row.forward_within_days ?? 7} 天入队
                        </span>
                      </div>
                      <p className="text-xs leading-relaxed text-slate-600">
                        <span className="font-medium text-slate-500">转发站点</span>
                        {siteLabels.length ? `：${siteLabels.join('、')}` : '：—'}
                      </p>
                      {staleLabels.length > 0 ? (
                        <p className="text-xs leading-relaxed text-amber-800">
                          <span className="font-medium">失效站点</span>
                          <span className="text-amber-700/90">（已关闭或不在展示范围，编辑保存时将自动移除）</span>
                          ：{staleLabels.join('、')}
                        </p>
                      ) : null}
                      <p className="text-[11px] text-slate-500">
                        <span className="font-medium text-slate-600">小程序</span>
                        {' · '}
                        发圈 {row.moments_run_time_range || '07:00-22:00'} · 群发 {row.group_run_time_range || '07:00-22:00'}
                        {' · '}
                        近 {row.forward_within_days ?? 7} 天入队
                      </p>
                      <p className="text-[11px] text-teal-800">
                        <span className="font-medium">微推广</span>
                        {' · '}
                        发圈 {row.mp_moments_run_time_range || '07:00-22:00'} · 群发{' '}
                        {row.mp_group_run_time_range || '07:00-22:00'}
                        {(row.promotion_area_ids?.length ?? 0) > 0
                          ? ` · 已绑定 ${row.promotion_area_ids?.length} 个区域`
                          : ' · 未绑定推广区域'}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-1.5 sm:justify-end">
                      <Button type="button" variant="secondary" className="!px-3 !py-1.5 text-xs" onClick={() => openEdit(row)}>
                        编辑
                      </Button>
                      <Button
                        type="button"
                        variant={row.enabled ? 'secondary' : 'primary'}
                        className="!px-3 !py-1.5 text-xs"
                        onClick={() => void toggleRow(row, !row.enabled)}
                      >
                        {row.enabled ? '停用' : '启用'}
                      </Button>
                      <Button type="button" variant="danger" className="!px-3 !py-1.5 text-xs" onClick={() => void removeRow(row)}>
                        删除
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {total > perPage ? (
          <PaginationBar
            className="mt-4"
            page={page}
            totalPages={pages}
            total={total}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(pages, p + 1))}
          />
        ) : null}
      </Card>

      <Modal
        open={modalOpen}
        wide="xl"
        title={editingId ? `编辑机器人 #${editingId}` : '新增机器人'}
        onClose={closeModal}
        footer={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {formTab !== 'basic' ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="!px-3 !py-1.5 text-xs"
                  disabled={saving}
                  onClick={() =>
                    setFormTab(formTab === 'micropromo' ? 'miniprogram' : 'basic')
                  }
                >
                  上一步
                </Button>
              ) : null}
              {formTab !== 'micropromo' ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="!px-3 !py-1.5 text-xs"
                  disabled={saving}
                  onClick={() =>
                    setFormTab(formTab === 'basic' ? 'miniprogram' : 'micropromo')
                  }
                >
                  下一步
                </Button>
              ) : null}
              <span className="text-xs text-slate-500">保存时将提交全部标签页中的配置</span>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={closeModal} disabled={saving}>
                取消
              </Button>
              <Button type="button" variant="primary" onClick={() => void submitForm()} disabled={saving}>
                {saving ? '保存中…' : '保存'}
              </Button>
            </div>
          </div>
        }
      >
        <SegmentedTabs
          ariaLabel="机器人配置分段"
          value={formTab}
          onChange={setFormTab}
          items={ROBOT_FORM_TABS}
          className="mb-4"
        />
        {formMsg ? <p className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{formMsg}</p> : null}

        {formTab === 'basic' ? (
          <div className="space-y-4" role="tabpanel">
            <p className="text-sm text-slate-600">填写机器人名称与总开关。渠道、站点等请在「小程序转发」「微推广」标签中配置。</p>
            <label className={`${labelClass} block`}>
              <span>名称</span>
              <input className={inputClass} value={robotName} onChange={(e) => setRobotName(e.target.value)} autoFocus />
            </label>
            <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>
                <span className="font-medium text-slate-900">启用机器人</span>
                <span className="mt-0.5 block text-xs text-slate-500">关闭后小程序与微推广转发任务均不会下发给该设备</span>
              </span>
            </label>
          </div>
        ) : null}

        {formTab === 'miniprogram' ? (
          <div className="space-y-4" role="tabpanel">
            <p className="text-sm text-slate-600">勾选站点后，按小程序入队任务与下列渠道、时段规则转发。</p>
            <div>
              <span className={labelClass}>需要转发的站点</span>
              <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">
                列表与小程序「展示站点」范围一致（仅含已启用站点）。若历史配置中含有已关闭或已移出展示范围的站点，会在下方「失效站点」中列出，保存时将自动移除。
              </p>
              {formStaleSites.length > 0 ? (
                <div
                  className="mt-2 rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2.5 text-sm text-amber-950"
                  role="status"
                >
                  <p className="font-medium">失效站点（{formStaleSites.length}）</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-amber-900/90">
                    以下站点已关闭或已从本小程序展示范围移除，无法继续勾选；点击「保存」后将自动从本机器人转发范围中清除。
                  </p>
                  <ul className="mt-2 space-y-1 text-xs">
                    {formStaleSites.map((s) => (
                      <li key={s.id} className="flex flex-wrap items-center gap-1.5">
                        <span className="line-through decoration-amber-700/60">{s.site_name?.trim() || `站点 #${s.id}`}</span>
                        <span className="font-mono text-[11px] text-amber-800/80">#{s.id}</span>
                        {s.enabled === false ? (
                          <span className="rounded-full bg-amber-200/80 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
                            已关闭
                          </span>
                        ) : (
                          <span className="rounded-full bg-amber-200/80 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
                            已移出展示
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="mt-2 max-h-52 space-y-0.5 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/40 p-2">
                {robotSiteSelectRows.length === 0 ? (
                  <p className="px-1 py-2 text-sm text-slate-500">暂无可选站点，请先在小程序管理中为该小程序勾选展示站点。</p>
                ) : (
                  robotSiteSelectRows.map((row) => (
                    <label
                      key={row.id}
                      className={`flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-white ${
                        row.level > 0 ? 'border-l-2 border-indigo-200 bg-white/80' : ''
                      }`}
                      style={{ paddingLeft: row.level > 0 ? 8 + row.level * 14 : 8 }}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 shrink-0"
                        checked={!!siteIdsSel[row.id]}
                        onChange={(e) => setSiteIdsSel((m) => ({ ...m, [row.id]: e.target.checked }))}
                      />
                      <span className="min-w-0 flex-1 leading-snug">
                        <span className="font-medium text-slate-900">{row.site_name}</span>
                        <span className="ml-1.5 font-mono text-[11px] font-normal text-slate-500">#{row.id}</span>
                        {row.level === 0 ? (
                          <span className="ml-2 inline-flex items-center rounded-full bg-slate-200/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                            一级
                          </span>
                        ) : (
                          <span className="ml-2 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-800">
                            二级
                          </span>
                        )}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={enableMoments} onChange={(e) => setEnableMoments(e.target.checked)} />
              转发朋友圈
            </label>
            <label className="mt-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={enableGroup} onChange={(e) => setEnableGroup(e.target.checked)} />
              转发微信群
            </label>
            <label className={`${labelClass} mt-3 block`}>
              <span>发圈时间</span>
              <input className={inputClass} value={momentsRange} onChange={(e) => setMomentsRange(e.target.value)} placeholder="07:00-22:00" />
            </label>
            <label className={`${labelClass} mt-2 block`}>
              <span>群发时间</span>
              <input className={inputClass} value={groupRange} onChange={(e) => setGroupRange(e.target.value)} placeholder="07:00-22:00" />
            </label>
            <p className="text-[11px] leading-relaxed text-slate-500">
              仅作用于<strong className="font-medium text-slate-600">小程序转发</strong>任务（与「微推广」标签中的时段相互独立）。
              超过结束时间后 Android 不再新启动对应发圈/群发；已在微信里进行中的操作不会中途打断。格式：07:00-22:00。
            </p>
            <label className={`${labelClass} mt-3 block`}>
              <span>转发几天内信息</span>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={180}
                  step={1}
                  className={inputClass + ' max-w-[7rem]'}
                  value={forwardWithinDays}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setForwardWithinDays(Number.isFinite(n) ? n : 7);
                  }}
                />
                <span className="text-sm text-slate-600">天</span>
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                按转发任务<strong className="font-medium text-slate-600">入队时间</strong>计算；超过该天数的队列任务 Android
                端将不再拉取（默认 7 天，可填 1–180）。
              </p>
            </label>
            <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <h4 className="text-sm font-semibold text-slate-900">自定义转发正文模板</h4>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                用于 Android 机器人执行小程序转发时粘贴的文字。未开启对应开关时不粘贴任何文字（不会使用信息原文）。微信群发优先使用
                #小程序:// 短链；模板中可含短链作为兜底。
              </p>
              <label className={`${labelClass} mt-3 block`}>
                <span className="sr-only">模板内容</span>
                <textarea
                  className={`${inputClass} min-h-[100px] font-mono text-xs`}
                  value={tpl}
                  onChange={(e) => setTpl(e.target.value)}
                  placeholder={'{title_text}\n{body_text}\n联系电话：{contact_phone}'}
                />
              </label>
              <div className="mt-2 rounded-md border border-slate-100 bg-slate-50/80 p-3">
                <p className="text-[11px] font-medium text-slate-700">可用变量（点击插入）</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {MINIPRO_FORWARD_TPL_VARS.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      className="rounded border border-slate-200 bg-white px-2 py-0.5 font-mono text-[11px] text-slate-800 hover:border-indigo-300 hover:bg-indigo-50"
                      onClick={() => setTpl((t) => t + `{${v.key}}`)}
                    >
                      {`{${v.key}}`}
                    </button>
                  ))}
                </div>
                <dl className="mt-3 space-y-1.5 border-t border-slate-200/80 pt-2">
                  {MINIPRO_FORWARD_TPL_VARS.map((v) => (
                    <div key={v.key} className="grid grid-cols-[auto_1fr] gap-x-2 text-[11px] leading-snug">
                      <dt className="font-mono font-medium text-indigo-800">{`{${v.key}}`}</dt>
                      <dd className="text-slate-600">{v.hint}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              <div className="mt-4 divide-y divide-slate-100 rounded-md border border-slate-200">
                <label className="flex cursor-pointer items-start justify-between gap-3 p-3 hover:bg-slate-50/80">
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-slate-900">发圈时使用</span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      开启后，朋友圈编辑页附言为模板渲染结果；关闭则不粘贴文字
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    className="mt-1 shrink-0"
                    checked={tplUseMoments}
                    onChange={(e) => setTplUseMoments(e.target.checked)}
                  />
                </label>
                <label className="flex cursor-pointer items-start justify-between gap-3 p-3 hover:bg-slate-50/80">
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-slate-900">群发时使用</span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      开启后，无独立短链时以模板渲染结果作为文件传输助手粘贴内容；关闭则不粘贴整段文案
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    className="mt-1 shrink-0"
                    checked={tplUseGroups}
                    onChange={(e) => setTplUseGroups(e.target.checked)}
                  />
                </label>
              </div>
            </div>
          </div>
        ) : null}

        {formTab === 'micropromo' ? (
          <div className="space-y-4" role="tabpanel">
            <p className="text-sm text-slate-600">
              绑定推广区域后，该机器人可承接对应区域的图文/招聘推广任务；与「微推广管理 → 配置机器人」双向同步。保存时会同步提交「小程序转发」中的站点；若存在失效站点将自动移除。
            </p>
            <div>
              <span className={labelClass}>绑定的推广区域</span>
              <div className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-lg border border-teal-200/80 bg-teal-50/40 p-2">
                {promoAreas.length === 0 ? (
                  <p className="px-1 py-2 text-sm text-slate-500">暂无推广区域，请先在微推广管理中创建。</p>
                ) : (
                  promoAreas.map((a) => (
                    <label key={a.id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={!!areaIdsSel[a.id]}
                        onChange={(e) => setAreaIdsSel((m) => ({ ...m, [a.id]: e.target.checked }))}
                      />
                      <span className="font-medium text-slate-900">{a.name}</span>
                      {!a.enabled ? <span className="text-xs text-amber-700">（区域已停用）</span> : null}
                    </label>
                  ))
                )}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={mpEnableMoments} onChange={(e) => setMpEnableMoments(e.target.checked)} />
              微推广 · 转发朋友圈
            </label>
              <label className="mt-2 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={mpEnableGroup} onChange={(e) => setMpEnableGroup(e.target.checked)} />
                微推广 · 转发微信群
              </label>
              <label className={`${labelClass} mt-3 block`}>
                <span>微推广发圈时间</span>
                <input className={inputClass} value={mpMomentsRange} onChange={(e) => setMpMomentsRange(e.target.value)} placeholder="07:00-22:00" />
              </label>
              <label className={`${labelClass} mt-2 block`}>
                <span>微推广群发时间</span>
                <input className={inputClass} value={mpGroupRange} onChange={(e) => setMpGroupRange(e.target.value)} placeholder="07:00-22:00" />
              </label>
              <p className="text-[11px] leading-relaxed text-teal-900/90">
                仅作用于<strong className="font-medium">微推广</strong>任务（与「小程序转发」标签中的时段相互独立）。
                超过结束时间后 Android 不再新启动对应发圈/群发；已在微信里进行中的操作不会中途打断。格式：07:00-22:00。
              </p>
              <label className={`${labelClass} mt-3 block`}>
                <span>微推广 · 转发几天内信息</span>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={180}
                    className={inputClass + ' max-w-[7rem]'}
                    value={mpForwardWithinDays}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      setMpForwardWithinDays(Number.isFinite(n) ? n : 7);
                    }}
                  />
                  <span className="text-sm text-slate-600">天</span>
                </div>
              </label>
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={mpUseTpl} onChange={(e) => setMpUseTpl(e.target.checked)} />
                微推广自定义转发正文模板
              </label>
              {mpUseTpl ? (
                <>
                  <textarea
                    className={`${inputClass} mt-1 min-h-[80px] font-mono text-xs`}
                    value={mpTpl}
                    onChange={(e) => setMpTpl(e.target.value)}
                    placeholder="{display_text} {contact_phone}"
                  />
                  <div className="mt-1 flex flex-wrap gap-1">
                    {MP_TPL_KEYS.map((k) => (
                      <button
                        key={k}
                        type="button"
                        className="rounded border border-slate-200 bg-white px-2 py-0.5 font-mono text-[11px] hover:bg-teal-50"
                        onClick={() => setMpTpl((t) => t + `{${k}}`)}
                      >
                        {`{${k}}`}
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={mpUseImg} onChange={(e) => setMpUseImg(e.target.checked)} />
                微推广专用发圈默认图
              </label>
              {mpUseImg ? (
                <div className="mt-2 space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    disabled={mpImgUploading}
                    onChange={(e) => void onPickMpImage(e.target.files?.[0] ?? null)}
                  />
                  {mpImgPreview ? <img src={mpImgPreview} alt="" className="max-h-32 rounded border" /> : null}
                </div>
              ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );

  return embedded ? <div className="space-y-0">{inner}</div> : <div className="space-y-6">{inner}</div>;
}
